import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { formatZAR } from "@/lib/constants";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "checking", data: null, jobId: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: "No session id", jobId: null });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "success", data, jobId: null });
          // Find the job id from the transaction's metadata on backend — we can infer via any completed paid tx; simpler: just show a generic success
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "failed", data: "Session expired", jobId: null });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 6) {
          setState({ phase: "timeout", data, jobId: null });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ phase: "error", data: e.response?.data?.detail || "Unknown error", jobId: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
      {state.phase === "checking" && (
        <>
          <Loader2 size={48} className="animate-spin text-[#3CFF7E] mb-4" />
          <h1 className="font-display text-2xl font-black">Confirming payment...</h1>
          <p className="text-sm text-[#9CA3AF] mt-2">Please wait while we verify with Stripe.</p>
        </>
      )}

      {state.phase === "success" && (
        <>
          <CheckCircle size={56} className="text-[#3CFF7E] mb-4" />
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#3CFF7E] font-display mb-1">PAID</div>
          <h1 className="font-display text-3xl font-black">Payment successful</h1>
          <p className="text-sm text-[#9CA3AF] mt-2">
            {state.data?.amount ? `${formatZAR(state.data.amount)} charged.` : "Your job is now marked as paid."}
          </p>
          <Link to="/app/requests" data-testid="back-to-requests-btn" className="mt-6 h-12 px-6 bg-[#0B1E5B] text-white rounded-md font-display font-bold uppercase tracking-wider text-sm flex items-center">
            Back to Requests
          </Link>
        </>
      )}

      {(state.phase === "failed" || state.phase === "error") && (
        <>
          <XCircle size={56} className="text-[#F87171] mb-4" />
          <h1 className="font-display text-3xl font-black">Payment not completed</h1>
          <p className="text-sm text-[#9CA3AF] mt-2">{String(state.data)}</p>
          <button onClick={() => navigate(-1)} className="mt-6 h-12 px-6 border border-white rounded-md font-display font-bold uppercase tracking-wider text-sm">
            Go Back
          </button>
        </>
      )}

      {state.phase === "timeout" && (
        <>
          <Loader2 size={40} className="text-[#9CA3AF] mb-4" />
          <h1 className="font-display text-2xl font-black">Still processing</h1>
          <p className="text-sm text-[#9CA3AF] mt-2">We'll update the job once Stripe confirms.</p>
          <Link to="/app/requests" className="mt-6 h-12 px-6 border border-white rounded-md font-display font-bold uppercase tracking-wider text-sm flex items-center">
            Back to Requests
          </Link>
        </>
      )}
    </div>
  );
}
